/**
 * File upload/download, serial numbers, and unit serial numbers API calls.
 * Phase 25 + 25b.
 */

import client from './client';

// ── Files ────────────────────────────────────────────────────────────

export async function fetchFiles(params = {}) {
  const { data } = await client.get('/files/', { params });
  return data.data || data.results || [];
}

export async function uploadFile(formData) {
  const { data } = await client.post('/files/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.data;
}

export async function uploadFileFromUrl(payload) {
  const { data } = await client.post('/files/upload-from-url/', payload);
  return data.data;
}

export async function updateFile(fileId, payload) {
  const { data } = await client.patch(`/files/${fileId}/`, payload);
  return data.data;
}

export async function deleteFile(fileId) {
  await client.delete(`/files/${fileId}/`);
}

export async function downloadFile(fileId) {
  const { data } = await client.get(`/files/${fileId}/download/`);
  return data.data; // { download_url, filename, mime_type }
}

/**
 * Open a file in a new browser tab, handling auth for local-served files.
 * Fetches the download URL, then if it's a local serve URL, fetches the
 * actual bytes through the authenticated client and opens as a blob URL.
 */
export async function openFileInTab(fileId) {
  const info = await downloadFile(fileId);
  if (!info?.download_url) throw new Error('No download URL');

  // If the URL is a relative /api/ path (local storage), fetch through auth client
  if (info.download_url.startsWith('/api/') || info.download_url.includes('/api/files/')) {
    const resp = await client.get(info.download_url, { responseType: 'blob' });
    const blob = new Blob([resp.data], { type: info.mime_type || 'application/octet-stream' });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    // Clean up after a delay so the tab has time to load
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } else {
    // R2 signed URL — open directly
    window.open(info.download_url, '_blank');
  }
}

export async function searchDocuments(params = {}) {
  const { data } = await client.get('/files/', { params });
  return data.data || data.results || [];
}


// ── Component Serial Numbers ─────────────────────────────────────────

export async function fetchSerialNumbers(params = {}) {
  const { data } = await client.get('/files/serial-numbers/', { params });
  return data.data || data.results || [];
}

export async function createSerialNumber(payload) {
  const { data } = await client.post('/files/serial-numbers/', payload);
  return data.data;
}

export async function updateSerialNumber(id, payload) {
  const { data } = await client.patch(`/files/serial-numbers/${id}/`, payload);
  return data.data;
}

export async function deleteSerialNumber(id) {
  await client.delete(`/files/serial-numbers/${id}/`);
}


// ── Unit Serial Numbers ──────────────────────────────────────────────

export async function fetchUnitSerialNumbers(params = {}) {
  const { data } = await client.get('/files/unit-serial-numbers/', { params });
  return data.data || data.results || [];
}

export async function generateUnitSerials(jobId) {
  const { data } = await client.post(`/jobs/${jobId}/generate-unit-serials/`);
  return data.data || [];
}

export async function updateUnitSerialNumber(id, payload) {
  const { data } = await client.patch(`/files/unit-serial-numbers/${id}/`, payload);
  return data.data || data;
}
